import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import { clearAuth } from "../../../utils/authStorage";
import {
  APPLICATION_STATUS_CHIPS,
  KYC_BADGE,
  STATUS_BADGE,
  formatDateTime,
  isKycVerified,
  isUnauthorized,
  kycLabel,
  marketsLabel,
  phoneHref,
  telegramHref,
  whatsappHref,
} from "./masterApplicationUtils";
import "../Home/Homepage.css";
import "./CopyTradingManagementPage.css";
import "./MasterApplicationsPage.css";

const MasterApplicationDetailPage = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [confirmPromote, setConfirmPromote] = useState(false);

  const goBack = useCallback(() => {
    navigate("/dashboard/copy-trading/master-applications");
  }, [navigate]);

  const applyPayload = (payload) => {
    setData(payload);
    setAdminNotes(payload?.adminNotes || "");
    setRejectReason(payload?.rejectReason || "");
  };

  const fetchDetail = useCallback(async () => {
    if (!applicationId) {
      toast.error("Invalid application ID");
      goBack();
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getCopyTradingMasterApplicationById(applicationId);
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return;
      }
      const status = Number(res?.httpStatus);
      if (status === 400 || status === 404) {
        toast.error(res?.message || (status === 404 ? "Application not found" : "Invalid application ID"));
        goBack();
        return;
      }
      const payload = res?.data && !Array.isArray(res.data) ? res.data : null;
      if (!payload) {
        setData(null);
        setError(res?.message || "Failed to fetch application");
        return;
      }
      applyPayload(payload);
    } catch (err) {
      setData(null);
      setError(err?.message || "Failed to fetch application");
    } finally {
      setLoading(false);
    }
  }, [applicationId, goBack]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const updateStatus = async (status) => {
    if (!applicationId || saving) return;
    if (status === "rejected" && !String(rejectReason || "").trim()) {
      toast.error("Add a reject reason to show the user.");
      return;
    }
    setSaving(true);
    try {
      const res = await AdminService.updateCopyTradingMasterApplicationStatus(applicationId, {
        status,
        adminNotes,
        rejectReason: status === "rejected" ? rejectReason : "",
      });
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return;
      }
      if (res?.success && res.data) {
        applyPayload(res.data);
        toast.success(res.message || `Application marked as ${status}`);
      } else {
        toast.error(res?.message || "Failed to update application status");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update application status");
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async () => {
    const userId = data?.nextStep?.promoteBody?.userId || data?.userId || data?.user?.userId;
    if (!userId || promoting) return;
    setPromoting(true);
    try {
      const res = await AdminService.updateMasterTradeAccount(userId, true);
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return;
      }
      if (!res?.success) {
        toast.error(res?.message || "Failed to promote user");
        return;
      }
      toast.success(res.message || "User account converted to master trade account");
      setConfirmPromote(false);
      const mark = await AdminService.updateCopyTradingMasterApplicationStatus(applicationId, {
        status: "converted",
        adminNotes,
        rejectReason: "",
      });
      if (mark?.success && mark.data) {
        applyPayload(mark.data);
      } else {
        await fetchDetail();
      }
    } catch (err) {
      toast.error(err?.message || "Failed to promote user");
    } finally {
      setPromoting(false);
    }
  };

  const user = data?.user || {};
  const kyc = user.kycVerified ?? data?.kycVerified;
  const alreadyMaster = Boolean(user.isMasterAccount || data?.isMasterAccount);
  const verified = isKycVerified(kyc);
  const callHref = phoneHref(data?.countryCode, data?.mobileNumber);
  const waHref = whatsappHref(data?.countryCode, data?.mobileNumber, data?.whatsapp);
  const tgHref = telegramHref(data?.telegram);
  const status = String(data?.status || "").toLowerCase();

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/copy-trading/master-applications">Master Applications</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>View</span>
          </p>
          <h1 className="dashboard-hero__title">Master Application</h1>
          <p className="dashboard-hero__subtitle">
            Review the form, contact the applicant, then promote them to a master trade account.
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-file-alt" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="ma-toolbar">
          <Link to="/dashboard/copy-trading/master-applications" className="ma-back">
            <i className="fas fa-arrow-left" /> Back to Master Applications
          </Link>
        </div>

        {loading && !data ? (
          <div className="copy-trading-table__empty">
            <i className="fas fa-spinner fa-spin" /> Loading…
          </div>
        ) : error && !data ? (
          <div className="ma-empty">
            <p>{error}</p>
            <button type="button" className="copy-trading-refresh-btn" onClick={fetchDetail}>
              Retry
            </button>
          </div>
        ) : data ? (
          <>
            <header className="ma-head">
              <div>
                <h2>{data.fullName || "—"}</h2>
                <p>
                  <span>{data.uuid || "—"}</span>
                  <span>{data.email || "—"}</span>
                </p>
              </div>
              <span className={`badge ${STATUS_BADGE[status] || ""}`}>{status || "—"}</span>
            </header>

            <p className="ma-note">
              This form does not create a master account. Call them first, mark <strong>contacted</strong>,
              then use Promote (same as Copy Trading Management). After the master ID exists, mark{" "}
              <strong>converted</strong>.
            </p>

            <div className="ma-actions">
              {callHref ? (
                <a className="copy-trading-view-btn" href={callHref}>
                  <i className="fas fa-phone" /> Call
                </a>
              ) : null}
              {waHref ? (
                <a className="copy-trading-followers-btn" href={waHref} target="_blank" rel="noreferrer">
                  <i className="fab fa-whatsapp" /> WhatsApp
                </a>
              ) : null}
              {tgHref ? (
                <a className="copy-trading-config-btn" href={tgHref} target="_blank" rel="noreferrer">
                  <i className="fas fa-paper-plane" /> Telegram
                </a>
              ) : null}
              <button
                type="button"
                className="copy-trading-promote-btn"
                onClick={() => setConfirmPromote(true)}
                disabled={promoting || alreadyMaster || !verified}
                title={
                  alreadyMaster
                    ? "Already a master account"
                    : verified
                      ? "Promote via PUT /master-trade-account"
                      : "KYC must be verified before promotion"
                }
              >
                <i className="fas fa-user-plus" /> {alreadyMaster ? "Already a master" : "Promote to master"}
              </button>
            </div>

            <div className="ma-grid">
              <section className="ma-card">
                <h3>Application form</h3>
                <dl className="ma-dl">
                  <dt>Full name</dt>
                  <dd>{data.fullName || "—"}</dd>
                  <dt>Email</dt>
                  <dd>{data.email || "—"}</dd>
                  <dt>Mobile</dt>
                  <dd>{[data.countryCode, data.mobileNumber].filter(Boolean).join(" ") || "—"}</dd>
                  <dt>Telegram</dt>
                  <dd>{data.telegram || "—"}</dd>
                  <dt>WhatsApp</dt>
                  <dd>{data.whatsapp || "—"}</dd>
                  <dt>Experience</dt>
                  <dd>
                    {data.experienceYears != null ? `${data.experienceYears} years` : "—"}
                    {data.tradingExperience ? ` · ${data.tradingExperience}` : ""}
                  </dd>
                  <dt>Markets</dt>
                  <dd>{marketsLabel(data.preferredMarkets)}</dd>
                  <dt>Submitted</dt>
                  <dd>{formatDateTime(data.submittedAt)}</dd>
                  <dt>Contacted</dt>
                  <dd>{formatDateTime(data.contactedAt)}</dd>
                  <dt>Converted</dt>
                  <dd>{formatDateTime(data.convertedAt)}</dd>
                  <dt>Rejected</dt>
                  <dd>{formatDateTime(data.rejectedAt)}</dd>
                </dl>
                {data.message ? (
                  <p className="ma-message" style={{ marginTop: "0.85rem" }}>
                    {data.message}
                  </p>
                ) : null}
              </section>

              <section className="ma-card">
                <h3>Live account</h3>
                <dl className="ma-dl">
                  <dt>Name</dt>
                  <dd>{[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}</dd>
                  <dt>UUID</dt>
                  <dd>{user.uuid || "—"}</dd>
                  <dt>Email</dt>
                  <dd>{user.email || "—"}</dd>
                  <dt>Mobile</dt>
                  <dd>{[user.countryCode, user.mobileNumber].filter(Boolean).join(" ") || "—"}</dd>
                  <dt>KYC</dt>
                  <dd>
                    <span className={`badge ${KYC_BADGE[Number(kyc)] || "badge--pending"}`}>
                      {kycLabel(kyc)}
                    </span>
                  </dd>
                  <dt>Account</dt>
                  <dd>{user.accountStatus || user.status || "—"}</dd>
                  <dt>Copy role</dt>
                  <dd>{user.copyTradingRole || (alreadyMaster ? "master" : "none")}</dd>
                  <dt>Master?</dt>
                  <dd>{alreadyMaster ? "Yes" : "No"}</dd>
                </dl>
                {user.userId ? (
                  <Link
                    to={`/dashboard/trader/${user.userId}`}
                    className="copy-trading-view-btn"
                    style={{ marginTop: "0.9rem", display: "inline-flex" }}
                  >
                    Open trader profile
                  </Link>
                ) : null}
              </section>
            </div>

            <section className="ma-status-form">
              <h3>Lead status (CRM only)</h3>
              <p className="copy-trading-header__hint" style={{ margin: "0 0 0.85rem" }}>
                Changing status here does not create or remove the master role.
              </p>
              <div className="ma-status-pills">
                {APPLICATION_STATUS_CHIPS.filter((c) => c.value !== "all").map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    className={`copy-trading-tab ${status === chip.value ? "copy-trading-tab--active" : ""}`}
                    disabled={saving || status === chip.value}
                    onClick={() => updateStatus(chip.value)}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <label>
                Admin notes
                <textarea
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Called on WhatsApp, will promote after KYC check"
                />
              </label>
              <label>
                Reject reason
                <textarea
                  rows={2}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Shown back to the user if rejected"
                />
              </label>
              <button
                type="button"
                className="copy-trading-refresh-btn"
                onClick={() => updateStatus(status || "pending")}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save notes"}
              </button>
            </section>
          </>
        ) : null}
      </div>

      {confirmPromote ? (
        <div
          className="copy-trading-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => !promoting && setConfirmPromote(false)}
        >
          <div className="copy-trading-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="copy-trading-modal__title">
              <i className="fas fa-user-plus" /> Promote to master?
            </h3>
            <p className="copy-trading-modal__text">
              This calls the existing promote API for {data?.fullName || "this user"}, then marks the
              application as converted. Configure their master settings afterwards on Copy Trading
              Management.
            </p>
            <div className="copy-trading-modal__actions">
              <button
                type="button"
                className="copy-trading-modal__cancel"
                onClick={() => setConfirmPromote(false)}
                disabled={promoting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="copy-trading-promote-btn"
                onClick={handlePromote}
                disabled={promoting}
              >
                {promoting ? "Promoting…" : "Promote & mark converted"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default MasterApplicationDetailPage;
